import { useTranslation } from 'react-i18next'
import { AiChatSelectionMode } from '@amitable/shared/ai-chat-decisions'
import type { Ref } from 'react'
import type {
  AiChatDecisionAnswer,
  AiChatDecisionOption,
  AiChatDecisionQuestion as DecisionQuestion,
} from '@amitable/shared/ai-chat-decisions'
import { cn } from '@/shared/lib/utils'

type QuestionProps = {
  requestId: string
  question: DecisionQuestion
  answer: AiChatDecisionAnswer
  disabled: boolean | undefined
  onChange: (answer: AiChatDecisionAnswer) => void
  headingRef?: Ref<HTMLLegendElement>
}

function selectedOptions(answer: AiChatDecisionAnswer, optionId: string) {
  if (answer.selectedOptionIds.includes(optionId))
    return answer.selectedOptionIds.filter((id) => id !== optionId)

  return [...answer.selectedOptionIds, optionId]
}

function DecisionOption({
  requestId,
  question,
  answer,
  disabled,
  onChange,
  option,
}: QuestionProps & { option: AiChatDecisionOption }) {
  const { t } = useTranslation('aiChat')
  const multiple = question.selectionMode === AiChatSelectionMode.MULTIPLE
  const checked = answer.selectedOptionIds.includes(option.id)

  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2 rounded-md border p-3 break-words',
        checked ? 'border-primary bg-primary/5' : 'border-border',
        disabled && 'cursor-default',
      )}
    >
      <input
        type={multiple ? 'checkbox' : 'radio'}
        name={`${requestId}-${question.id}`}
        value={option.id}
        checked={checked}
        className="mt-1 shrink-0 accent-primary"
        onChange={() =>
          onChange({
            ...answer,
            selectedOptionIds: multiple
              ? selectedOptions(answer, option.id)
              : [option.id],
            customText: multiple ? answer.customText : null,
          })
        }
      />
      <span className="min-w-0">
        <span className="font-medium">{option.label}</span>
        {question.recommendedOptionId === option.id ? (
          <span className="ml-2 text-xs text-muted-foreground">
            {t('decisionRecommended')}
          </span>
        ) : null}
        {option.description ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            {option.description}
          </span>
        ) : null}
      </span>
    </label>
  )
}

export function AiChatDecisionQuestion(props: QuestionProps) {
  const { requestId, question, answer, disabled, onChange, headingRef } = props
  const { t } = useTranslation('aiChat')
  const multiple = question.selectionMode === AiChatSelectionMode.MULTIPLE
  const customId = `decision-${requestId}-${question.id}-custom`

  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-2">
      <legend
        ref={headingRef}
        tabIndex={-1}
        className="font-medium outline-none"
      >
        {question.heading}
      </legend>
      <p>{question.question}</p>
      <p className="text-xs text-muted-foreground">
        {t(multiple ? 'decisionSelectMany' : 'decisionSelectOne')}
      </p>
      <div className="grid gap-2">
        {question.options.map((option) => (
          <DecisionOption key={option.id} {...props} option={option} />
        ))}
      </div>
      <label htmlFor={customId} className="block text-xs font-medium">
        {t('decisionCustom')}
      </label>
      <textarea
        id={customId}
        value={answer.customText ?? ''}
        maxLength={4000}
        rows={2}
        className="w-full rounded-md border border-input bg-background p-2 text-sm disabled:opacity-70"
        onChange={(event) => {
          const customText = event.target.value
          onChange({
            ...answer,
            customText,
            selectedOptionIds:
              !multiple && customText.trim() ? [] : answer.selectedOptionIds,
          })
        }}
      />
    </fieldset>
  )
}
